<template>
    <TooltipProvider :delay-duration="300">
        <!-- `inert` while the small-screen gate is up. The gate paints over this, but a
             purely visual cover still leaves everything under it focusable and clickable
             by keyboard — inert takes the whole subtree out of the tab order, out of hit
             testing and out of the accessibility tree, which is what makes the gate's
             `aria-modal` true rather than just asserted. -->
        <div :inert="isSmallScreenBlocked || undefined">
            <v-transition
                :animation-duration="animationDuration"
                appear
                :disabled="isAnimationDisabled || pageStateChanging"
                :enter-animation="enterAnimation"
                :leave-animation="leaveAnimation"
                :mode="animationMode"
            >
                <router-view v-if="pageStateVisible" :key="pageStateRevision" />
            </v-transition>
            <!-- Single global upload tray — every upload surface (Documents page,
                 VDocumentUploader, …) pushes into `uploads/` and renders here. -->
            <v-upload-tray
                :items="$store.state.uploads.items"
                @dismiss="$store.dispatch('uploads/dismiss')"
            />
        </div>
        <!-- Last child so it paints over the app chrome, tooltips and the upload tray:
             below MIN_SUPPORTED_WIDTH the layout isn't usable, so we cover it. -->
        <v-small-screen-gate v-model:blocked="isSmallScreenBlocked" />
    </TooltipProvider>
</template>

<script>
    import debounce from 'lodash.debounce';
    import { TooltipProvider } from 'reka-ui';

    import { PRODUCTION } from '@/store/constants';
    import { logError } from '@/constants';
    import { mapState } from 'vuex';
    import { needsLogin } from './router';

    import VSmallScreenGate from '@/components/elements/VSmallScreenGate.vue';
    import VTransition from '@/components/elements/VTransition.vue';
    import VUploadTray from '@/components/elements/VUploadTray.vue';

    export default {
        name: 'app',
        components: {
            TooltipProvider,
            VSmallScreenGate,
            VTransition,
            VUploadTray,
        },
        data() {
            return {
                // Owned by VSmallScreenGate; drives `inert` on the content it covers.
                isSmallScreenBlocked: false,
            };
        },
        computed: {
            pageStateVisible() {
                return __DEV_TOOLS__ ? this.$pageStateView?.visible !== false : true;
            },
            pageStateRevision() {
                return __DEV_TOOLS__ ? this.$pageStateView?.revision || 0 : 0;
            },
            pageStateChanging() {
                return __DEV_TOOLS__ ? this.$pageStateView?.changing || false : false;
            },
            ...mapState('transition_animation', [
                'animationDuration',
                'animationMode',
                'enterAnimation',
                'isAnimationDisabled',
                'leaveAnimation',
            ]),
        },
        watch: {
            // Every time a route changes, refresh the JWT token
            $route: debounce(function watchRouteChange() {
                this.$store.dispatch('refreshJWT');
            }, 5000),
            '$store.getters.isLoggedIn': {
                immediate: true,
                async handler(val) {
                    if (val) {
                        // Refresh products if the backend version has changed
                        this.$store.dispatch('configuration/checkProductsVersion');
                    } else if (needsLogin(this.$route)) {
                        // Pass the resolved route, not the bare path: with a string,
                        // every `to.name !== …` public-route exemption in needsLogin()
                        // is vacuously true, so any anonymous page (register,
                        // calculators, …) would bounce to login when this fires at
                        // mount — now deterministic since mount awaits router.isReady().
                        let next = this.$route.name === 'login' ? '/' : this.$route.path;
                        if (Object.keys(this.$route.query || {}).length) {
                            const query = [];
                            Object.keys(this.$route.query).forEach(key => {
                                query.push(`${key}=${this.$route.query[key]}`);
                            });
                            next = `${next}?${query.join('&')}`;
                        }
                        this.navigateTo({
                            name: 'login',
                            query: { next },
                        });
                    }
                },
            },
        },
        mounted() {
            try {
                if (PRODUCTION && window.Intercom) {
                    window.Intercom('boot');
                }
            } catch (e) {
                logError(e);
            }
        },
    };
</script>
